import React, { useState, useEffect, useCallback } from "react";
import { Form, Input, Spin } from "antd";
import { signUp, redeemSubscriptionCode, validateSubscriptionCode } from "../../utils/api";
import { LoadingOutlined, CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import GoogleSignInButton from "./GoogleSignInButton";

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [notMatchedError, setNotMatchedError] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [showCodeField, setShowCodeField] = useState(false);
  const [form] = Form.useForm();

  // Code validation state
  const [codeValidating, setCodeValidating] = useState(false);
  const [codeDetails, setCodeDetails] = useState(null);
  const [codeError, setCodeError] = useState("");

  // Debounce timer ref
  const debounceTimerRef = React.useRef(null);

  const validateCode = useCallback(async (code) => {
    if (!code || code.trim().length < 10) {
      setCodeDetails(null);
      setCodeError("");
      return;
    }

    setCodeValidating(true);
    setCodeError("");
    setCodeDetails(null);

    try {
      const res = await validateSubscriptionCode(code.trim());

      if (res?.data?.statusCode === 200 && res?.data?.data?.valid) {
        setCodeDetails(res.data.data);
        setCodeError("");
      } else {
        setCodeDetails(null);
        setCodeError(res?.response?.data?.message || res?.data?.message || "Invalid code");
      }
    } catch (err) {
      setCodeDetails(null);
      setCodeError("Failed to validate code");
    } finally {
      setCodeValidating(false);
    }
  }, []);

  const handleCodeChange = useCallback(
    (e) => {
      const value = e.target.value;

      // Clear previous timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce validation by 500ms
      debounceTimerRef.current = setTimeout(() => {
        validateCode(value);
      }, 500);
    },
    [validateCode],
  );

  useEffect(() => {
    setIsVisible(true);
    // Pre-fill subscription code from URL parameter if present
    const codeFromUrl = searchParams.get("code");
    if (codeFromUrl) {
      setShowCodeField(true);
      form.setFieldsValue({ subscriptionCode: codeFromUrl });
      // Validate the pre-filled code
      validateCode(codeFromUrl);
    }
  }, [searchParams, form, validateCode]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const onFinish = async (values) => {
    const cleanedValues = {
      password: values.password.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      name: values.name.trim(),
      role: values.role,
    };
    const subscriptionCode = values.subscriptionCode?.trim();

    setLoading(true);
    setNotMatchedError("");

    try {
      const signUpRes = await signUp(cleanedValues);

      if (signUpRes?.response?.data?.statusCode === 400) {
        setNotMatchedError(signUpRes?.response?.data?.message);
        setLoading(false);
        return;
      }

      if (signUpRes?.data) {
        localStorage.setItem("token", signUpRes?.data?.data?.accessToken);
        if (signUpRes?.data?.data?.role) {
          localStorage.setItem("role", signUpRes?.data?.data?.role);
        }

        if (cleanedValues.role === "manager") {
          navigate("/profile");
          setLoading(false);
          return;
        }

        // If subscription code provided, try to redeem it
        if (subscriptionCode) {
          const redeemRes = await redeemSubscriptionCode(subscriptionCode);
          console.log("Redeem code response:", redeemRes);

          // Success is indicated by statusCode 200 and data.subscription existing
          if (redeemRes?.data?.statusCode === 200 || redeemRes?.data?.data?.subscription) {
            // Code redeemed successfully - go directly to the app
            navigate("/form_page");
          } else {
            // Code redemption failed - show error but still allow user to proceed
            const errorMsg =
              redeemRes?.response?.data?.message || redeemRes?.data?.message || "Invalid or expired subscription code";
            setNotMatchedError(errorMsg);
            setLoading(false);
            // User is registered, let them navigate to subscription page manually
            setTimeout(() => {
              navigate("/subscription");
            }, 3000);
          }
        } else {
          // No code provided, go to subscription page to purchase
          navigate("/subscription");
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNotMatchedError("An unexpected error occurred. Please try again.");
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <Spin
      spinning={loading}
      indicator={
        <LoadingOutlined
          style={{
            fontSize: 48,
            color: "white",
          }}
          spin
        />
      }
    >
      <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

        <div className="relative min-h-screen flex items-center justify-center px-4 py-12 pt-32">
          <div
            className={`w-full max-w-md transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {/* Card */}
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 md:p-10 shadow-2xl">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-4">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  <span className="text-sm text-gray-300">Free to get started</span>
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">Create Account</h1>
                <p className="text-gray-400">Start your vehicle appraisal journey today</p>
              </div>

              <Form
                form={form}
                name="register"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
                layout="vertical"
                className="space-y-1"
              >
                <Form.Item name="name" rules={[{ required: true, message: "Please enter your name" }]}>
                  <Input
                    placeholder="Full name"
                    className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
                    style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white" }}
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input
                    placeholder="Email address"
                    className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
                    style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white" }}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: "Please enter your password" },
                    { min: 6, message: "Password must be at least 6 characters" },
                  ]}
                >
                  <Input.Password
                    placeholder="Password"
                    className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus-within:bg-white/20 focus-within:border-cyan-400 transition-all [&>input]:bg-transparent [&>input]:text-white [&>input]:placeholder-gray-400 [&>input]:border-none [&>input]:focus:shadow-none"
                    style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white" }}
                  />
                </Form.Item>

                <Form.Item name="phone" rules={[{ required: true, message: "Please enter your phone number" }]}>
                  <Input
                    placeholder="Phone number"
                    type="tel"
                    className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
                    style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white" }}
                  />
                </Form.Item>
                {/* Subscription Code Toggle and Field */}
                {!showCodeField ? (
                  <div className="text-center py-2">
                    <button
                      type="button"
                      onClick={() => setShowCodeField(true)}
                      className="text-cyan-400 hover:text-cyan-300 text-sm font-medium transition-colors"
                    >
                      Have a subscription code?
                    </button>
                  </div>
                ) : (
                  <div className="mb-2">
                    <Form.Item name="subscriptionCode" className="mb-1">
                      <div className="relative">
                        <Input
                          placeholder="Enter subscription code (e.g., XXXX-XXXX-XXXX-XXXX)"
                          onChange={handleCodeChange}
                          className={`w-full px-4 py-3.5 bg-white/10 border rounded-xl text-white placeholder-gray-400 focus:bg-white/20 transition-all pr-20 ${
                            codeDetails
                              ? "border-green-500"
                              : codeError
                              ? "border-red-500"
                              : "border-white/20 focus:border-cyan-400"
                          }`}
                          style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white" }}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                          {codeValidating && <LoadingOutlined className="text-cyan-400" spin />}
                          {!codeValidating && codeDetails && <CheckCircleFilled className="text-green-500 text-lg" />}
                          {!codeValidating && codeError && <CloseCircleFilled className="text-red-500 text-lg" />}
                          <button
                            type="button"
                            onClick={() => {
                              setShowCodeField(false);
                              setCodeDetails(null);
                              setCodeError("");
                              form.setFieldsValue({ subscriptionCode: "" });
                            }}
                            className="text-gray-400 hover:text-white transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </Form.Item>

                    {/* Code validation feedback */}
                    {codeDetails && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 mt-2">
                        <div className="flex items-center gap-3">
                          <CheckCircleFilled className="text-green-500 text-lg" />
                          <div className="flex-1">
                            <p className="text-green-400 font-medium text-sm">{codeDetails.productName}</p>
                            {codeDetails.productDescription && (
                              <p className="text-gray-400 text-xs mt-0.5">{codeDetails.productDescription}</p>
                            )}
                            {codeDetails.durationDays && (
                              <p className="text-cyan-400 text-xs mt-0.5 font-medium">
                                {codeDetails.durationDays >= 365
                                  ? `${Math.round(codeDetails.durationDays / 365)} Year${
                                      Math.round(codeDetails.durationDays / 365) > 1 ? "s" : ""
                                    } Validity`
                                  : `${Math.round(codeDetails.durationDays / 30)} Month${
                                      Math.round(codeDetails.durationDays / 30) > 1 ? "s" : ""
                                    } Validity`}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {codeError && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mt-2">
                        <div className="flex items-center gap-2">
                          <CloseCircleFilled className="text-red-500" />
                          <p className="text-red-400 text-sm">{codeError}</p>
                        </div>
                      </div>
                    )}

                    {!codeDetails && !codeError && !codeValidating && (
                      <p className="text-gray-500 text-xs mt-1 text-center">
                        Enter your subscription code to skip payment
                      </p>
                    )}
                  </div>
                )}

                {notMatchedError && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 mb-4">
                    <p className="text-red-300 text-sm text-center">{notMatchedError}</p>
                  </div>
                )}

                <Form.Item className="mb-0 pt-2">
                  <button
                    type="submit"
                    className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transform hover:-translate-y-0.5"
                  >
                    Create Account
                  </button>
                </Form.Item>
              </Form>

              {/* Terms */}
              <p className="text-center text-gray-500 text-sm mt-4">
                By signing up, you agree to our{" "}
                <Link to="/terms" className="text-cyan-400 hover:text-cyan-300">
                  Terms
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300">
                  Privacy Policy
                </Link>
              </p>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/20"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-transparent text-gray-400">or sign up with</span>
                </div>
              </div>

              {/* Social Login */}
              <GoogleSignInButton
                onSuccess={() => navigate("/subscription")}
                onError={(error) => setNotMatchedError(error)}
                buttonText="Continue with Google"
                fullWidth
              />

              {/* Login Link */}
              <p className="text-center text-gray-400 mt-6">
                Already have an account?{" "}
                <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors">
                  Sign in
                </Link>
              </p>

              <p className="text-center text-gray-400 mt-2 text-sm">
                <Link to="/manager/register" className="text-gray-400 hover:text-white transition-colors">
                  Signup as manager?
                </Link>
              </p>
            </div>

            {/* Features list */}
            <div
              className={`flex flex-wrap justify-center gap-4 mt-8 text-gray-500 text-sm transition-all duration-700 delay-300 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              {["Free to start", "No credit card required", "Cancel anytime"].map((text, i) => (
                <div key={i} className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default Register;
